import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useFetchWalletTransferBatchDetailsMutation } from "../../../features/tenant/WalletTransfer/walletTransferApi";
import dayjs from "dayjs";
import { getVendorName } from "../../../utils/getVendorName";

const WalletTransferBatchDetails = () => {
    const { batch_id } = useParams();
    const navigate = useNavigate();

    const tenant_id = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
    const token = sessionStorage.getItem("access_token");

    const [fetchDetails, { isLoading }] = useFetchWalletTransferBatchDetailsMutation();

    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");

    const fetchData = async (currentPage, rowsPerPage, searchTerm) => {
        if (!batch_id) return;
        try {
            const response = await fetchDetails({
                token,
                tenant_id,
                batch_id,
                limit: rowsPerPage,
                offset: (currentPage - 1) * rowsPerPage,
                search: searchTerm
            }).unwrap();

            if (response.success) {
                setData(response.body.data);
                setTotalRows(Number(response.body.total));
            }
        } catch (error) {
            console.error("Failed to fetch batch details:", error);
        }
    };

    useEffect(() => {
        if (!batch_id) {
            navigate(`/genefied/${getVendorName()}/wallet-transfer-logs`);
            return;
        }
        fetchData(page, perPage, search);
    }, [page, perPage, batch_id]);

    const handlePageChange = (p) => setPage(p);
    const handlePerRowsChange = (newPerPage, p) => {
        setPerPage(newPerPage);
        setPage(p);
    };

    const onSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchData(1, perPage, search);
    };

    const columns = [
        {
            name: "User Details",
            cell: (row) => (
                <div className="flex flex-col py-2">
                    <span className="font-bold text-gray-800">{row.name}</span>
                    <span className="text-xs text-gray-500">{row.mobile}</span>
                </div>
            ),
            sortable: true,
            minWidth: "150px"
        },
        {
            name: "User Type",
            selector: (row) => row.app_user_type,
            sortable: true,
            cell: (row) => (
                <span className="capitalize px-2 py-1 rounded bg-blue-50 text-blue-700 text-[10px] font-bold">
                    {row.app_user_type}
                </span>
            )
        },
        {
            name: "Points Moved",
            selector: (row) => row.point_balance,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="font-mono font-bold text-blue-600">
                    {row.point_balance}
                </span>
            )
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <div className="flex flex-col">
                    <span className={`capitalize font-bold text-[10px] ${row.status === "success" ? "text-green-600" : "text-red-600"
                        }`}>
                        {row.status}
                    </span>
                    {row.error_message && (
                        <span className="text-[9px] text-red-400 leading-tight">
                            {row.error_message}
                        </span>
                    )}
                </div>
            )
        },
        {
            name: "Transfer Date",
            selector: (row) => dayjs(row.created_at).format("DD MMM YYYY, HH:mm"),
            sortable: true,
            minWidth: "160px"
        }
    ];

    return (
        <div className="flex w-full bg-gray-200 h-full justify-center">
            <div className="w-full">
                <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm flex justify-between items-center pr-6">
                    <div className="flex flex-col">
                        <span className="text-xl font-bold">Wallet Transfer Batch Details</span>
                        <span className="text-[10px] text-gray-400 font-mono tracking-widest">{batch_id}</span>
                    </div>
                    <div className="text-sm">
                        <LinksComponent />
                    </div>
                </div>

                <div className="p-4">
                    <div className="bg-white rounded-xl shadow-md p-4 mb-4 flex justify-between items-center">
                        <form onSubmit={onSearch} className="flex gap-2 w-full max-w-md">
                            <input
                                type="text"
                                placeholder="Search by name or mobile..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                                Search
                            </button>
                        </form>
                        <button
                            onClick={() => navigate(`/genefied/${getVendorName()}/wallet-transfer-logs`)}
                            className="text-gray-500 hover:text-gray-800 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                        >
                            ← Back to Logs
                        </button>
                    </div>

                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <DataTableComponent
                            columns={columns}
                            data={data}
                            loading={isLoading}
                            totalRows={totalRows}
                            handlePageChange={handlePageChange}
                            handlePerRowsChange={handlePerRowsChange}
                            paginationPerPage={perPage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletTransferBatchDetails;
